import { lazy } from 'react';
import { blogPostSets } from '../features/blogs';

// Lazy load all components
const components = {
  // Main components
  Home: lazy(() => import('../pages/Home')),
  AboutUs: lazy(() => import('../pages/AboutUs')),
  Curriculum: lazy(() => import('../pages/Curriculum')),
  AiTools: lazy(() => import('../pages/AiTools')),
  Blog: lazy(() => import('../pages/Blog')),
  Contact: lazy(() => import('../pages/Contact')),
  Challenges: lazy(() => import('../pages/career-quest/ChallengesPage')),
  CareerMaker: lazy(() => import('../pages/career-quest/CareerMakerPage')),

  // Dashboard components
  DashboardHome: lazy(() => import('../pages/dashboard/DashboardHome')),
  DashboardModules: lazy(() => import('../pages/dashboard/DashboardModules')),
  DashboardAI: lazy(() => import('../pages/dashboard/DashboardAI')),
  ModuleDetail: lazy(() => import('../pages/dashboard/ModuleDetail')),

  // Individual module pages
  CareerQuestPage: lazy(() => import('../pages/dashboard/CareerQuestPage')),
  FairyTalePage: lazy(() => import('../pages/dashboard/FairyTalePage')),
  MusicStudioPage: lazy(() => import('../pages/dashboard/MusicStudioPage')),
  CodingFromScratchPage: lazy(
    () => import('../pages/dashboard/CodingFromScratchPage')
  ),

  // Tool components
  Welcome: lazy(() => import('../pages/tools/Welcome')),
  HeroCreator: lazy(() => import('../pages/tools/HeroCreator')),
  HeroPresentation: lazy(
    () => import('../pages/tools/HeroCreatorPresentation')
  ),
  MonsterCreator: lazy(() => import('../pages/tools/MonsterCreator')),
  MonsterPresentation: lazy(
    () => import('../pages/tools/MonsterCreatorPresentation')
  ),
  WorldCreator: lazy(() => import('../pages/tools/WorldCreator')),
  WorldMapPresentation: lazy(
    () => import('../pages/tools/WorldMapCreatorPresentation')
  ),
  WorldMapDesignPresentation: lazy(
    () => import('../pages/tools/WorldMapDesignPresentation')
  ),
  StoryCreator: lazy(() => import('../pages/tools/StoryCreator')),
  StoryPresentation: lazy(
    () => import('../pages/tools/StoryCreatorPresentation')
  ),
  CareerQuest: lazy(() => import('../pages/tools/CareerQuest')),
  AlienBabyMaker: lazy(() => import('../pages/tools/AlienBabyMaker')),

  // Blog components - dynamically generate from blogPosts
  BlogPage: lazy(() => import('../pages/blogs/BlogPostPage')),
};

// Generate blog routes dynamically
const blogRoutes = blogPostSets.ALL.map(post => ({
  path: post.slug,
  component: components.BlogPage,
}));

export const ROUTES = {
  // Main Routes with components
  MAIN: [
    { path: '/', component: components.Home },
    { path: '/about', component: components.AboutUs },
    { path: '/curriculum', component: components.Curriculum },
    { path: '/ai-tools', component: components.AiTools },
    { path: '/blog', component: components.Blog },
    { path: '/contact', component: components.Contact },
  ],

  // Dashboard Routes
  DASHBOARD: {
    ROOT: '/dashboard',
    ROUTES: [
      { path: 'home', component: components.DashboardHome },
      // { path: 'modules', component: components.DashboardModules },
      { path: 'ai', component: components.DashboardAI },
      // Individual module pages (must come before generic :moduleId route)
      { path: 'modules/career-quest', component: components.CareerQuestPage },
      { path: 'modules/fairy-tale', component: components.FairyTalePage },
      { path: 'modules/music-studio', component: components.MusicStudioPage },
      {
        path: 'modules/coding-from-scratch',
        component: components.CodingFromScratchPage,
      },
      // { path: 'modules/:moduleId', component: components.ModuleDetail },
    ],
  },

  // Legal Routes with components
  LEGAL: [
    { path: '/terms', component: components.Home },
    { path: '/privacy', component: components.Home },
    { path: '/cookies', component: components.Home },
  ],

  // Tool Routes with components
  TOOLS: {
    ROOT: '/tools',
    ROUTES: [
      { path: 'welcome', component: components.Welcome },
      { path: 'hero-maker', component: components.HeroCreator },
      { path: 'hero-presentation', component: components.HeroPresentation },
      { path: 'monster-maker', component: components.MonsterCreator },
      {
        path: 'monster-presentation',
        component: components.MonsterPresentation,
      },
      { path: 'world-maker', component: components.WorldCreator },
      {
        path: 'world-map-presentation',
        component: components.WorldMapPresentation,
      },
      {
        path: 'world-map-design',
        component: components.WorldMapDesignPresentation,
      },
      { path: 'story-maker', component: components.StoryCreator },
      { path: 'story-presentation', component: components.StoryPresentation },
      { path: 'career-quest', component: components.CareerQuest },
      { path: 'alien-baby-maker', component: components.AlienBabyMaker },
    ],
  },

  // Career Quest Routes
  CAREER_QUEST: {
    ROOT: '/career-quest',
    ROUTES: [
      { path: 'challenges', component: components.Challenges },
      { path: 'career-maker', component: components.CareerMaker },
    ],
  },

  BLOGS: {
    ROOT: '/blogs',
    ROUTES: blogRoutes,
  },
};

// Export path constants for use in links
export const PATHS = {
  HOME: '/',
  ABOUT: '/about',
  CURRICULUM: '/curriculum',
  AI_TOOLS: '/ai-tools',
  BLOG: '/blog',
  CONTACT: '/contact',
  DASHBOARD: {
    ROOT: '/dashboard',
    HOME: '/dashboard/home',
    MODULES: '/dashboard/modules',
    AI: '/dashboard/ai',
    MODULE_DETAIL: moduleId => `/dashboard/modules/${moduleId}`,
    // Individual module pages
    CAREER_QUEST: '/dashboard/modules/career-quest',
    FAIRY_TALE: '/dashboard/modules/fairy-tale',
    MUSIC_STUDIO: '/dashboard/modules/music-studio',
    CODING_FROM_SCRATCH: '/dashboard/modules/coding-from-scratch',
  },
  LEGAL: {
    TERMS: '/terms',
    PRIVACY: '/privacy',
    COOKIES: '/cookies',
  },
  TOOLS: {
    ROOT: '/tools',
    WELCOME: '/tools/welcome',
    HERO_MAKER: '/tools/hero-maker',
    HERO_PRESENTATION: '/tools/hero-presentation',
    MONSTER_MAKER: '/tools/monster-maker',
    MONSTER_PRESENTATION: '/tools/monster-presentation',
    WORLD_MAKER: '/tools/world-maker',
    WORLD_MAP_PRESENTATION: '/tools/world-map-presentation',
    WORLD_MAP_DESIGN: '/tools/world-map-design',
    STORY_MAKER: '/tools/story-maker',
    STORY_PRESENTATION: '/tools/story-presentation',
    CAREER_QUEST: '/tools/career-quest',
    ALIEN_BABY_MAKER: '/tools/alien-baby-maker',
  },
  CAREER_QUEST: {
    ROOT: '/career-quest',
    CHALLENGES: '/career-quest/challenges',
    CAREER_MAKER: '/career-quest/career-maker',
  },
  BLOGS: {
    ROOT: '/blogs',
    ...Object.fromEntries(
      blogPostSets.ALL.map(post => [
        post._id.toUpperCase(),
        `/blogs/${post.slug}`,
      ])
    ),
  },
};
